import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const sanitizeHtml = (dirty) => ({ __html: DOMPurify.sanitize(String(dirty || ''), { USE_PROFILES: { html: true } }) });

const ageInDays = (dob) => {
    if (!dob) return Number.POSITIVE_INFINITY;
    const t = Date.parse(dob);
    return Number.isNaN(t) ? Number.POSITIVE_INFINITY : (Date.now() - t) / 86400000;
};

const formatPrintedOn = () => {
    const d = new Date();
    const pad = (n) => n.toString().padStart(2, '0');
    const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return `${weekdays[d.getDay()]} ${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * Print roster for renewed students.
 * URL params:
 *  - scope: class | program | branch  (required)
 *  - id: class_id or program_id (when scope is class|program)
 *  - branch: branch name (when scope is branch)
 *  - preview=1 (optional): skip auto-print
 */
const AllStudentsPrint = () => {
    const [sp] = useSearchParams();
    const scope = sp.get('scope');
    const targetId = sp.get('id');
    const targetBranch = sp.get('branch');
    const preview = sp.get('preview') === '1';

    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [programs, setPrograms] = useState([]);
    const [classes, setClasses] = useState([]);
    const [teachers, setTeachers] = useState([]);
    const [settings, setSettings] = useState({});

    useEffect(() => {
        (async () => {
            try {
                const [studRes, progRes, classRes, teachRes, setRes] = await Promise.all([
                    axios.get(`${API_URL}/api/students/renewed/list`, { withCredentials: true }),
                    axios.get(`${API_URL}/api/programs`, { withCredentials: true }),
                    axios.get(`${API_URL}/api/classes`, { withCredentials: true }),
                    axios.get(`${API_URL}/api/teachers`, { withCredentials: true }).catch(() => ({ data: [] })),
                    axios.get(`${API_URL}/api/settings`, { withCredentials: true }).catch(() => ({ data: {} })),
                ]);
                setRows(studRes.data || []);
                setPrograms(progRes.data || []);
                setClasses(classRes.data || []);
                setTeachers(teachRes.data || []);
                setSettings(setRes.data || {});
            } finally { setLoading(false); }
        })();
    }, []);

    useEffect(() => {
        if (loading || preview) return;
        const t = setTimeout(() => window.print(), 500);
        return () => clearTimeout(t);
    }, [loading, preview]);

    const classById = useMemo(() => Object.fromEntries(classes.map(c => [c.id, c])), [classes]);
    const programById = useMemo(() => Object.fromEntries(programs.map(p => [p.id, p])), [programs]);
    const teacherById = useMemo(() => Object.fromEntries(teachers.map(t => [t.id, t])), [teachers]);

    // Filter students to the requested scope, then group into classes (one class = one printed page).
    const pages = useMemo(() => {
        if (!scope || (scope !== 'branch' && !targetId) || (scope === 'branch' && !targetBranch)) return [];
        const inScope = rows.filter(r => {
            if (scope === 'class') return r.class_id === targetId;
            if (scope === 'program') return r.program_id === targetId;
            if (scope === 'branch') return (r.branch || '') === targetBranch;
            return false;
        });
        // Group by class_id
        const map = new Map();
        for (const s of inScope) {
            const key = s.class_id || `__noclass__::${s.class_name || 'Unassigned'}`;
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(s);
        }
        const entries = Array.from(map.entries()).map(([key, students]) => ({
            key,
            students: [...students].sort((a, b) => ageInDays(a.date_of_birth) - ageInDays(b.date_of_birth)),
        }));
        // Sort classes by program name then class name
        entries.sort((a, b) => {
            const aClass = classById[a.key] || {};
            const bClass = classById[b.key] || {};
            const aProg = (programById[aClass.program_id] || {}).name || a.students[0]?.program_name || '';
            const bProg = (programById[bClass.program_id] || {}).name || b.students[0]?.program_name || '';
            const cmp = aProg.localeCompare(bProg);
            if (cmp !== 0) return cmp;
            const aName = aClass.arabic_name || aClass.english_name || aClass.name || a.students[0]?.class_name || '';
            const bName = bClass.arabic_name || bClass.english_name || bClass.name || b.students[0]?.class_name || '';
            return aName.localeCompare(bName);
        });
        return entries;
    }, [rows, scope, targetId, targetBranch, classById, programById]);

    if (loading) {
        return <div className="flex items-center justify-center h-screen"><Loader2 className="h-8 w-8 animate-spin text-[#064E3B]" /></div>;
    }

    if (!scope) {
        return <div className="p-8 text-center text-[#6B7280]">Missing scope parameter</div>;
    }

    const logoUrl = settings.logo_url || `${API_URL}/api/public/logo`;
    const headerHtml = settings.report_header || `<h1 style="margin:0;font-size:20px;color:#064E3B;">${settings.school_name || 'Nurulhuda Quranic School'}</h1>`;
    const footerHtml = settings.report_footer || `<p style="margin:0;">info@nhq.ie</p>`;
    const printedOn = formatPrintedOn();

    // Compute grand total for footer of last page
    const grandTotal = pages.reduce((n, p) => n + p.students.length, 0);

    return (
        <>
            <style>{`
                @page { size: A4 portrait; margin: 5mm; }
                @media print {
                    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                    .no-print { display: none !important; }
                }
                body { background: #fff; margin: 0; }
                .print-root { font-family: Arial, sans-serif; color: #1F2937; padding: 0; }
                .page-section { page-break-after: always; break-after: page; padding: 4mm 4mm 6mm 4mm; }
                .page-section:last-of-type { page-break-after: auto; break-after: auto; }
                .p-header { display: flex; align-items: center; gap: 12px; border-bottom: 2px solid #064E3B; padding-bottom: 6px; margin-bottom: 6px; }
                .p-header img { max-height: 46px; max-width: 100px; }
                .printed-on { font-style: italic; font-size: 10px; color: #6B7280; margin: 2px 0 6px 0; }
                .title-line { font-size: 15px; font-weight: 700; color: #064E3B; margin: 0 0 2px 0; }
                .subtitle-line { font-size: 12px; color: #4B5563; margin: 0 0 8px 0; }
                table.roster { width: 100%; border-collapse: collapse; font-size: 11px; }
                table.roster th, table.roster td { border: 1px solid #D1D5DB; padding: 4px 6px; text-align: left; vertical-align: middle; }
                table.roster th { background: #064E3B; color: #fff; font-weight: 700; }
                table.roster tr:nth-child(even) td { background: #FAFAFA; }
                table.roster tfoot td { background: #F3F4F6; font-weight: 700; }
                .p-footer { margin-top: 8px; padding-top: 4px; border-top: 1px solid #E5E7EB; font-size: 10px; color: #6B7280; text-align: center; }
                .toolbar { position: sticky; top: 0; background: #064E3B; color: #fff; padding: 10px 14px; display: flex; justify-content: space-between; align-items: center; font-family: Arial, sans-serif; font-size: 13px; z-index: 10; }
                .toolbar button { background: #ffffff; color: #064E3B; border: 0; padding: 6px 14px; border-radius: 4px; cursor: pointer; font-weight: 600; font-size: 12px; }
            `}</style>

            <div className="toolbar no-print">
                <span>Print preview — {scope === 'class' ? 'Class roster' : scope === 'program' ? 'Program roster' : 'Branch roster'} — {pages.length} class(es), {grandTotal} student(s)</span>
                <div>
                    <button onClick={() => window.print()} data-testid="all-students-print-btn">Print</button>
                </div>
            </div>

            <div className="print-root" data-testid="all-students-print-body">
                {pages.length === 0 ? (
                    <div className="page-section">
                        <div className="p-header">
                            {logoUrl ? <img src={logoUrl} alt="Logo" onError={(e) => { e.target.style.display = 'none'; }} /> : null}
                            <div style={{ flex: 1 }} dangerouslySetInnerHTML={sanitizeHtml(headerHtml)} />
                        </div>
                        <p className="printed-on">Printed on: {printedOn}</p>
                        <p style={{ padding: '40px 0', textAlign: 'center', color: '#6B7280' }}>No renewed students in this scope yet.</p>
                        <div className="p-footer" dangerouslySetInnerHTML={sanitizeHtml(footerHtml)} />
                    </div>
                ) : (
                    pages.map(({ key, students }) => {
                        const cls = classById[key] || {};
                        const prog = programById[cls.program_id] || {};
                        const branch = prog.location || students[0]?.branch || '—';
                        const progName = prog.name || students[0]?.program_name || '—';
                        const room = cls.room || '—';
                        const classArabic = cls.arabic_name || cls.english_name || cls.name || students[0]?.class_name || '—';
                        const teacher = teacherById[cls.main_teacher_id] || {};
                        const teacherName = teacher.first_name ? `${teacher.first_name} ${teacher.last_name || ''}`.trim() : (cls.teacher_name || '—');
                        return (
                            <section key={key} className="page-section" data-testid={`print-class-${key}`}>
                                <div className="p-header">
                                    {logoUrl ? <img src={logoUrl} alt="Logo" onError={(e) => { e.target.style.display = 'none'; }} /> : null}
                                    <div style={{ flex: 1 }} dangerouslySetInnerHTML={sanitizeHtml(headerHtml)} />
                                </div>
                                <p className="printed-on">Printed on: {printedOn}</p>
                                <p className="title-line">{branch} — {progName}</p>
                                <p className="subtitle-line">{room} - <span dir="rtl" style={{ fontWeight: 700 }}>{classArabic}</span> - {teacherName}</p>
                                <table className="roster">
                                    <thead>
                                        <tr>
                                            <th style={{ width: '6%' }}>#</th>
                                            <th style={{ width: '10%' }}>Code</th>
                                            <th style={{ width: '34%' }}>Full Name</th>
                                            <th style={{ width: '20%' }}>Arabic Name</th>
                                            <th style={{ width: '15%' }}>DOB</th>
                                            <th style={{ width: '15%' }}>Gender</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {students.map((s, i) => (
                                            <tr key={s.id}>
                                                <td>{i + 1}</td>
                                                <td style={{ fontFamily: 'monospace' }}>{s.reg_code || '—'}</td>
                                                <td>{s.first_name} {s.last_name}</td>
                                                <td dir="rtl">{s.arabic_name || '—'}</td>
                                                <td>{s.date_of_birth || '—'}</td>
                                                <td style={{ textTransform: 'capitalize' }}>{s.gender || '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <td colSpan={5} style={{ textAlign: 'right' }}>Total students:</td>
                                            <td>{students.length}</td>
                                        </tr>
                                    </tfoot>
                                </table>
                                <div className="p-footer" dangerouslySetInnerHTML={sanitizeHtml(footerHtml)} />
                            </section>
                        );
                    })
                )}
            </div>
        </>
    );
};

export default AllStudentsPrint;
